import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { LoadingController, ToastController } from "@ionic/angular";
import { NgxPermissionsService } from "ngx-permissions";
import { BehaviorSubject, ReplaySubject, lastValueFrom } from "rxjs";
import { filter, take, timeout } from "rxjs/operators";
import { ROUTES } from "src/app/utils/routes";
import {
  AppSettingService,
  AuthenticationService,
  UserService,
  UserSettingModel,
  UserSettingService,
} from "../swagger";
import { StorageService } from "../storage.service";
import { clearAllConversionDrafts } from "../forms/create-conversion.form";
import { clearAllCustomerOrderDrafts } from "../forms/create-order.form";
import { clearAllInvTranDrafts } from "../forms/create-inventory-transaction.form";
import { clearAllTransferInvDrafts } from "../forms/create-transfer-inventory.form";
import { TokenStorageService } from "../token-auth/token-storage.service";
import { TenantStorageService } from "../token-auth/tenant-storage.service";
import { formatPermissions } from "src/app/utils/formatPermissions";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { App } from "@capacitor/app";

@Injectable({
  providedIn: "root",
})
export class UserDataService {
  token: string | any;
  isNotGoToLogin: boolean;
  isNotResetUser: boolean;
  user$: ReplaySubject<any> = new ReplaySubject<any>(1);
  allTenants$: ReplaySubject<any> = new ReplaySubject<any>(1);
  userSettings$: ReplaySubject<UserSettingModel[]> = new ReplaySubject<
    UserSettingModel[]
  >(1);
  permissions$: ReplaySubject<any> = new ReplaySubject<any>(1);
  defaultPage: string = ROUTES.HELP_PAGE;
  debounceTime$: BehaviorSubject<number> = new BehaviorSubject<number>(1000);

  constructor(
    private tokenStorage: TokenStorageService,
    private tenantStorageService: TenantStorageService,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private userService: UserService,
    private appSettingService: AppSettingService,
    private userSettingService: UserSettingService,
    private ngxPermissionsService: NgxPermissionsService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private storageService: StorageService,
  ) {
    this.tokenStorage.token$.subscribe(async (token) => {
      if (token) {
        await this.initUser(token);
      } else {
        if (!this.isNotResetUser) {
          this.user$.next(undefined);
          this.permissions$.next(undefined);
          this.ngxPermissionsService.flushPermissions();
        }
        if (this.token) {
          this.token = undefined;
          // Previously logged in so re-route
          if (this.router.url != ROUTES.LOGIN_PAGE && !this.isNotGoToLogin) {
            await this.router.navigateByUrl(ROUTES.LOGIN_PAGE);
          }
        }
      }
    });
    this.tenantStorageService.allTenants$.subscribe(async (tenants) => {
      if (tenants) {
        this.allTenants$.next(tenants);
      } else {
        this.allTenants$.next(undefined);
      }
    });
  }

  async initUser(token: string, reload: boolean = false) {
    try {
      if (this.token != token || reload) {
        this.token = token;
        let user = await this._getCurrentUser(token);
        if (user && user.userId) {
          // permissions work
          let permissions = await this._getUserPermission(user.userId);
          let permissionsString: string[] = permissions
            ? formatPermissions(permissions)
            : [];
          this.permissions$.next(permissionsString);
          this.ngxPermissionsService.loadPermissions(permissionsString);
          let settings = await this._getUserSettings(user.userId);
          this.userSettings$.next(settings);
          let debounceTime: any = await this._getAppDebounceTimeSettings();
          if (
            debounceTime &&
            debounceTime.appSettingValue &&
            parseFloat(debounceTime.appSettingValue)
          ) {
            const debounceAppSettingValue =
              parseFloat(debounceTime.appSettingValue) &&
              parseFloat(debounceTime.appSettingValue) > 0
                ? parseFloat(debounceTime.appSettingValue)
                : 1000;
            this.debounceTime$.next(debounceAppSettingValue);
          }
        }
        this.user$.next(user);
      }
    } catch (e) {
      this.user$.next(undefined);
      this.ngxPermissionsService.flushPermissions();
      this.permissions$.next(undefined);
      this.userSettings$.next(undefined);
    }
  }

  async reloadUser() {
    let loadingView = await this.loadingCtrl.create({});
    await loadingView.present();
    try {
      let user = await this.getCurrentUser();
      // permissions work
      let permissions = await this._getUserPermission(user.userId);
      let permissionsString: string[] = permissions
        ? formatPermissions(permissions)
        : [];
      this.permissions$.next(permissionsString);
      this.ngxPermissionsService.loadPermissions(permissionsString);
      this.user$.next(user);
      await loadingView.dismiss();
    } catch (e) {
      await loadingView.dismiss();
      throw e;
    }
  }

  async getAuthorizeUrl() {
    try {
      let res =
        await this.authenticationService.apiAuthenticationAuthorizationURLGet({
          isApp: Capacitor.getPlatform() === "web" ? false : true,
        });
      let result = await lastValueFrom(res);
      if (result && result.url) {
        if (Capacitor.getPlatform() === "web") {
          window.location.href = result.url;
        } else {
          await Browser.open({
            url: result.url,
          });
          App.addListener("appUrlOpen", async ({ url }) => {
            if (url) {
              // Parse the URL to extract the query parameters
              const params = new URL(url);
              const code = params.searchParams.get("code");
              const sessionState = params.searchParams.get("session_state");
              const state = params.searchParams.get("state");
              if (code && sessionState && state) {
                this.router.navigate([ROUTES.CALLBACK_PAGE], {
                  queryParams: {
                    code: code,
                    session_state: sessionState,
                    state: state,
                  },
                });
              }
            }
            await Browser.close();
          });
        }
      }
    } catch (e) {
      throw e;
    }
  }

  async login(callback: any) {
    this.tokenStorage.setToken(null);
    if (callback && callback.accessToken) {
      if (
        !callback.tenantProfiles ||
        (callback.tenantProfiles && callback.tenantProfiles.length === 0)
      ) {
        let { key } = {
          key: callback.accessToken,
        };
        await this.tokenStorage.setToken(key);
        try {
          await this.user$
            .pipe(
              timeout(30000),
              filter((u) => u != undefined),
              take(1),
            )
            .toPromise();
        } catch (e) {
          this.tenantStorageService.setTenants(null);
          await this.tokenStorage.setToken(null);
          throw e;
        }
      } else if (callback.tenantProfiles && callback.tenantProfiles.length) {
        this.tenantStorageService.setTenants({
          token: callback.accessToken,
          userEmail: callback.userEmail,
          tenantProfiles: callback.tenantProfiles,
        });
        setTimeout(() => {
          this.router.navigateByUrl(ROUTES.MULTITENANT_ACCESS_PAGE);
        }, 200);
      }
    } else if (callback.error) {
      this.loadingCtrl.dismiss();
      this.tenantStorageService.setTenants(null);
      await this.tokenStorage.setToken(null);
      this.router.navigateByUrl(ROUTES.LOGIN_PAGE);
      await this.openToast(callback.error);
    }
  }

  async openToast(message: string) {
    if (message) {
      let toast = await this.toastCtrl.create({
        message: message,
        buttons: [{ text: "Done", role: "cancel" }],
        duration: 10000,
        color: "primary",
      });
      await toast.present();
    }
  }

  async logout() {
    // Autosaved drafts live on the device, not on the account — clear them so
    // the next person signing in on a shared tablet isn't offered someone
    // else's unsaved work.
    await clearAllConversionDrafts(this.storageService);
    await clearAllInvTranDrafts(this.storageService);
    await clearAllTransferInvDrafts(this.storageService);
    await clearAllCustomerOrderDrafts(this.storageService);
    await this.tenantStorageService.setTenants(null);
    await this.tokenStorage.setToken(null);
    this.user$.next(undefined);
    this.permissions$.next(undefined);
    this.userSettings$.next(undefined);
    this.ngxPermissionsService.flushPermissions();
    this.token = null;
  }

  private async _getCurrentUser(toke: string): Promise<any> {
    let loadingView = await this.loadingCtrl.create({
      message: "Please wait, <br/> you are signing in",
    });
    await loadingView.present();
    try {
      let authType = await lastValueFrom(
        this.authenticationService.apiAuthenticationAuthorizationTypeGet(),
      );
      let user: any;
      if (authType === "AzureAuth") {
        user = await lastValueFrom(
          this.authenticationService.apiAuthenticationSignInUsingTokenPost({
            authAccessTokenModel: {
              accessToken: toke,
            },
          }),
        );
      } else if (authType === "EmailAuth") {
        user = await lastValueFrom(
          this.authenticationService.apiAuthenticationSignInAuthEmailUsingTokenPost(
            {
              authAccessTokenModel: {
                accessToken: toke,
              },
            },
          ),
        );
      }
      await loadingView.dismiss();
      return user;
    } catch (e) {
      await this.logout();
      await this.router.navigateByUrl(ROUTES.LOGIN_PAGE);
      await loadingView.dismiss();
      throw e;
    }
  }

  async _getUserPermission(userId: number): Promise<any> {
    // let loadingView = await this.loadingCtrl.create();
    // await loadingView.present();
    try {
      let permissions = (await lastValueFrom(
        this.userService.apiUserUserPermissionsGet({
          id: userId,
        }),
      )) as any;
      // await loadingView.dismiss();
      return permissions.permissions;
    } catch (e) {
      // await loadingView.dismiss();
      throw e;
    }
  }

  async _getUserSettings(userId: number): Promise<any> {
    // let loadingView = await this.loadingCtrl.create();
    // await loadingView.present();
    try {
      let settings = await lastValueFrom(
        this.userSettingService.apiUserSettingGetAllByUserIdIdGet({
          id: userId,
        }),
      );
      if (settings && settings.length) {
        const defaultPageFound = settings.find(
          (setting) =>
            setting.userSettingName === "DefaultPage" &&
            setting.userSettingValue,
        );
        if (defaultPageFound && defaultPageFound.userSettingValue) {
          this.defaultPage = defaultPageFound.userSettingValue;
        } else {
          this.defaultPage = ROUTES.HELP_PAGE;
        }
      } else {
        this.router.navigateByUrl(ROUTES.HELP_PAGE);
      }
      // await loadingView.dismiss();
      return settings;
    } catch (e) {
      // await loadingView.dismiss();
      throw e;
    }
  }

  get searchDebounceTime(): number {
    return this.debounceTime$.getValue();
  }

  async _getAppDebounceTimeSettings() {
    try {
      return await lastValueFrom(
        this.appSettingService.apiAppsettingByGroupAndNameGet({
          appSettingGroup: "APP",
          appSettingName: "SearchDebounceTime",
        }),
      );
    } catch (e) {
      return null;
    }
  }

  async getMultiTenants(): Promise<any> {
    return this.allTenants$.pipe(take(1)).toPromise();
  }

  async getCurrentUser(): Promise<any> {
    return this.user$.pipe(take(1)).toPromise();
  }

  // getCurrentUserSettings only carries the "Profile" settings that
  // GetAllByUserId returns, so anything stored under another type has to be
  // fetched by type and name.
  async getUserSettingValue(
    userSettingType: string,
    userSettingName: string,
  ): Promise<string | undefined> {
    const user = await this.getCurrentUser();
    if (!user || !user.userId) return undefined;
    const setting: any = await lastValueFrom(
      this.userSettingService.apiUserSettingGetUserSettingGet({
        userId: user.userId,
        userSettingType: userSettingType,
        userSettingName: userSettingName,
      }),
    );
    return setting && setting.userSettingValue
      ? setting.userSettingValue
      : undefined;
  }

  async getCurrentUserSettings(): Promise<any> {
    return this.userSettings$.pipe(take(1)).toPromise();
  }

  async getUserPermissions(): Promise<any> {
    return this.permissions$.pipe(take(1)).toPromise();
  }
}
